import { type PropsWithChildren, type ReactNode } from 'react';
import { type SupportedLocale } from '@zep-us/i18n';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { useTranslation } from 'next-i18next';

//FIXME: WithLabel 컴포넌트 공통으로 추출 or Select 컴포넌트
import { type QuizTypeOption } from '@/api/ai/useBuildAiQuiz';
import { useLocale } from '@/hooks/useLocale';
import { CheckSmallCircleIcon, MessageLinesIcon, RotateCircleIcon } from '@/icons';
import { LocalizedOxTypeIcon } from '@/shared/components/LocalizedOxTypeIcon';
import { type GenerateNumberOption, MIXED_DIFFICULTY_VALUE, QUIZ_LEVEL_OPTIONS, type QuizLevelOption } from '@/shared/features/ai-quiz-generation/constants';
import { SingleSelect } from '@/ui_v3/components/select';
export const SelectAiQuizCount = ({
  selectedCount,
  onSelect,
  isQuotaLimited
}: {
  selectedCount: number;
  onSelect: (v: GenerateNumberOption) => void;
  isQuotaLimited?: boolean;
}) => {
  const {
    t
  } = useTranslation(['common', 'build']);
  const quizCountOptions: {
    value: string;
    label: ReactNode;
    disabled?: boolean;
  }[] = [{
    value: '10',
    label: <SelectOption>
          {t('build:ai.addQuiz.questionQuantity', {
        n: 10
      })}
        </SelectOption>
  }, {
    value: '15',
    label: <SelectOption>
          {t('build:ai.addQuiz.questionQuantity', {
        n: 15
      })}
        </SelectOption>,
    disabled: isQuotaLimited
  }, {
    value: '20',
    label: <SelectOption>
          {t('build:ai.addQuiz.questionQuantity', {
        n: 20
      })}
        </SelectOption>,
    disabled: isQuotaLimited
  }, {
    value: '30',
    label: <SelectOption>
          {t('build:ai.addQuiz.questionQuantity', {
        n: 30
      })}
        </SelectOption>,
    disabled: isQuotaLimited
  }];
  return <SingleSelect options={quizCountOptions} value={selectedCount.toString()} onValueChange={v => {
    if (v === null) return;
    onSelect(Number(v) as GenerateNumberOption);
  }} title={t('build:build.ai.quizCount')} data-sentry-element="SingleSelect" data-sentry-component="SelectAiQuizCount" data-sentry-source-file="BuildOption.tsx" />;
};
export const SelectAiQuizType = ({
  selectedQuizType,
  onSelect
}: {
  selectedQuizType: QuizTypeOption;
  onSelect: (v: QuizTypeOption) => void;
}) => {
  const {
    t
  } = useTranslation(['build']);
  const quizTypeOptions: {
    value: QuizTypeOption;
    label: ReactNode;
  }[] = [{
    value: 'mixed',
    label: <SelectOption>
          <RotateCircleIcon size="xs" />
          {t('build.ai.mixed')}
        </SelectOption>
  }, {
    value: 'multiple',
    label: <SelectOption>
          <CheckSmallCircleIcon size="xs" />
          {t('build.multiple.button')}
        </SelectOption>
  }, {
    value: 'subjective',
    label: <SelectOption>
          <MessageLinesIcon size="xs" />
          {t('build.subjective.button')}
        </SelectOption>
  }, {
    value: 'ox',
    label: <SelectOption>
          <LocalizedOxTypeIcon size="xs" fill={ICON_COLOR.default} />
          {t('build.ox.button')}
        </SelectOption>
  }];
  return <SingleSelect options={quizTypeOptions} value={selectedQuizType} onValueChange={v => {
    if (v === null) return;
    onSelect(v as QuizTypeOption);
  }} title={t('ai.type.title')} data-sentry-element="SingleSelect" data-sentry-component="SelectAiQuizType" data-sentry-source-file="BuildOption.tsx" />;
};
const SelectOption = ({
  children
}: PropsWithChildren) => <div className="flex items-center gap-3xs" data-sentry-component="SelectOption" data-sentry-source-file="BuildOption.tsx">{children}</div>;
export const SelectAiQuizLevel = ({
  selectedQuizLevel,
  onSelect
}: {
  selectedQuizLevel: QuizLevelOption;
  onSelect: (v: QuizLevelOption) => void;
}) => {
  const {
    t
  } = useTranslation(['build']);

  // 아이콘은 "문제 유형 섞기" (SelectAiQuizType) 와 동일하게 RotateCircleIcon
  // 을 사용해 시각적 일관성을 유지한다.
  const quizLevelOptions: {
    value: QuizLevelOption;
    label: ReactNode;
  }[] = QUIZ_LEVEL_OPTIONS.map((option: QuizLevelOption) => ({
    value: option,
    label: option === MIXED_DIFFICULTY_VALUE ? <SelectOption>
            <RotateCircleIcon size="xs" />
            {t('ai.difficulty.mixed')}
          </SelectOption> : t(`ai.difficulty.${option}` as const)
  }));
  return <SingleSelect options={quizLevelOptions} value={selectedQuizLevel} onValueChange={v => {
    if (v === null) return;
    onSelect(v as QuizLevelOption);
  }} title={t('ai.difficulty.title')} data-sentry-element="SingleSelect" data-sentry-component="SelectAiQuizLevel" data-sentry-source-file="BuildOption.tsx" />;
};
export const SelectAiLanguage = ({
  selectedLanguage,
  onSelect
}: {
  selectedLanguage: SupportedLocale;
  onSelect: (v: SupportedLocale) => void;
}) => {
  const {
    t
  } = useTranslation(['build', 'common']);
  const {
    locales
  } = useLocale();
  const languageOptions: {
    value: SupportedLocale;
    label: string;
  }[] = locales.map(locale => ({
    value: locale,
    label: t(`common:common.language.${locale}`, locale)
  }));
  return <SingleSelect options={languageOptions} value={selectedLanguage} onValueChange={v => {
    if (v === null) return;
    onSelect(v as SupportedLocale);
  }} title={t('build:build.ai.printLanguage')} data-sentry-element="SingleSelect" data-sentry-component="SelectAiLanguage" data-sentry-source-file="BuildOption.tsx" />;
};